/**
 * Whop Payments API slice (international checkout + billing portal).
 * Leaf module (Perf 3.1.b/e Step 6). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { fetchWithAuth, ApiError, getAuthStore } from './client';

export const whopApi = {
  // Create a Whop checkout session for Plus subscription.
  // When `isExtend` is true, the server attaches extend metadata so the webhook
  // carries forward the user's remaining Plus days and terminates any old
  // subscription (Whop or cross-processor like Paddle).
  createPlusCheckout: async ({
    planLength,
    source = 'web',
    isExtend = false,
    allowPromoCodes = true,
    checkoutPurpose = 'plus',
  }) => {
    const authStore = getAuthStore().getState();
    const userId = authStore.user?.id;
    const email = authStore.user?.email;

    const data = await fetchWithAuth('/json/payments/whop/checkout', {
      method: 'POST',
      body: {
        type: 'plus',
        planLength, // 'monthly' | 'yearly' | 'premium-monthly' | 'premium-yearly'
        userId,
        email,
        source,
        isExtend: !!isExtend,
        allowPromoCodes: allowPromoCodes !== false,
        checkoutPurpose,
      }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to create Whop checkout session', 400, data);
    }

    // Handle various response formats from backend
    // Backend returns: { sessionId, planId } for embed, or { checkoutUrl } for redirect
    const sessionId = data.data?.sessionId || data.sessionId;
    const planId = data.data?.planId || data.planId;
    const checkoutUrl = data.data?.checkoutUrl || data.checkoutUrl;

    return { sessionId, planId, checkoutUrl };
  },

  // Create a Whop checkout session for credit pack purchase
  createCreditsCheckout: async ({ creditPackId, source = 'web' }) => {
    const authStore = getAuthStore().getState();
    const userId = authStore.user?.id;
    const email = authStore.user?.email;

    const data = await fetchWithAuth('/json/payments/whop/checkout', {
      method: 'POST',
      body: {
        type: 'credits',
        creditPackId,
        userId,
        email,
        source,
      }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to create Whop checkout session', 400, data);
    }

    // Handle various response formats from backend
    // Backend returns: { sessionId, planId } for embed, or { checkoutUrl } for redirect
    const sessionId = data.data?.sessionId || data.sessionId;
    const planId = data.data?.planId || data.planId;
    const checkoutUrl = data.data?.checkoutUrl || data.checkoutUrl;

    return { sessionId, planId, checkoutUrl };
  },

  // Get Whop billing portal URL for managing subscription & payment methods
  // International users use this to manage their subscription, update payment, cancel, view invoices
  getBillingPortalUrl: async () => {
    const data = await fetchWithAuth('/json/account/whop-portal', {
      method: 'GET',
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to get Whop billing portal URL', 400, data);
    }

    return {
      portalUrl: data.data?.portalUrl || data.portalUrl,
      manageUrl: data.data?.manageUrl || data.manageUrl,
    };
  },

  // Cancel a Whop Plus subscription directly via our backend. We do this
  // server-side because Whop's hosted manage portal can hide the cancel
  // action behind Extend/Upgrade.
  cancelWhopSubscription: async (reason) => {
    const data = await fetchWithAuth('/json/account/cancel-whop', {
      method: 'POST',
      body: { reason: reason?.trim() },
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to cancel subscription', 400, data);
    }

    const payload = data.data || data;
    return {
      canceledDate: payload.canceledDate,
      recurringEnabled: payload.recurringEnabled,
      purchaseId: payload.purchaseId,
      purchaseIds: payload.purchaseIds,
      accessUntil: payload.accessUntil,
    };
  },

  // Fallback geolocation for visitors with no server-resolved country. Prefer
  // the `countryCode` our own backend puts on the user payload — it is derived
  // from the Cloudflare edge, costs nothing and has no quota. ipapi.co's free
  // tier is 1000 requests/day, so this path is expected to fail regularly.
  //
  // A failure resolves to international (Whop) rather than domestic: Whop can
  // charge cards worldwide, while Valmar only clears US cards, so guessing
  // "domestic" strands every foreign buyer on a form that cannot succeed.
  // Failures are not cached, so a later attempt can still resolve the country.
  _ipCheckCache: null,
  _GEO_CACHE_KEY: 'gab_geo_cache_v1',
  _GEO_CACHE_TTL_MS: 7 * 24 * 60 * 60 * 1000, // 7 days
  _fetchCurrentIp: async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      if (!response.ok) return null;
      const data = await response.json();
      return data.ip || null;
    } catch {
      return null;
    }
  },
  _readGeoCache: () => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(whopApi._GEO_CACHE_KEY);
      if (!raw) return null;
      const cached = JSON.parse(raw);
      if (!cached || typeof cached.ip !== 'string') return null;
      if (Date.now() - cached.cachedAt > whopApi._GEO_CACHE_TTL_MS) return null;
      return cached;
    } catch {
      return null;
    }
  },
  checkIsInternational: async () => {
    // Return cached result if available
    if (whopApi._ipCheckCache !== null) {
      return whopApi._ipCheckCache;
    }

    const currentIp = await whopApi._fetchCurrentIp();
    const cached = whopApi._readGeoCache();

    // Reuse cached geo result if the IP hasn't changed — no ipapi call needed
    if (cached && cached.ip === currentIp) {
      whopApi._ipCheckCache = cached.isInternational;
      return cached.isInternational;
    }
    // If we couldn't determine the current IP, fall back to any fresh cache
    if (cached && currentIp === null) {
      whopApi._ipCheckCache = cached.isInternational;
      return cached.isInternational;
    }

    try {
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });

      if (!response.ok) {
        console.warn('[whopApi] IP geolocation failed, assuming domestic');
        // Don't persist the fallback — only in-memory for this session
        whopApi._ipCheckCache = false;
        return false;
      }

      const data = await response.json();
      const countryCode = data.country_code || data.country;

      // Rate-limit and error bodies come back HTTP 200 with no country.
      if (!countryCode) {
        console.warn('[whopApi] IP geolocation returned no country, routing to international checkout:', data?.reason || data?.error || '');
        return true;
      }

      const isInternational = countryCode !== 'US';

      console.log('[whopApi] IP geolocation result:', {
        country: countryCode,
        isInternational,
        city: data.city,
        region: data.region,
      });

      whopApi._ipCheckCache = isInternational;

      // Persist only successful lookups, keyed by IP
      if (countryCode) {
        try {
          window.localStorage.setItem(whopApi._GEO_CACHE_KEY, JSON.stringify({
            ip: data.ip || currentIp,
            countryCode,
            isInternational,
            city: data.city,
            region: data.region,
            cachedAt: Date.now(),
          }));
        } catch {
          // localStorage unavailable/unwritable — in-memory cache still applies
        }
      }

      return isInternational;
    } catch (error) {
      console.warn('[whopApi] IP geolocation error, routing to international checkout:', error);
      return true;
    }
  },

  // Clear the IP check cache (useful for testing or when user changes network)
  clearIpCache: () => {
    whopApi._ipCheckCache = null;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(whopApi._GEO_CACHE_KEY);
      } catch {
        // ignore
      }
    }
  },
};
